// src/utils/http.jsx
import { logout, isTokenExpired } from "./auth";


const ACTIVE_REQUESTS_KEY = "flpa_active_requests";

function incActiveRequest() {
  const n = Number(localStorage.getItem(ACTIVE_REQUESTS_KEY) || "0");
  localStorage.setItem(ACTIVE_REQUESTS_KEY, String(n + 1));
}

function decActiveRequest() {
  const n = Number(localStorage.getItem(ACTIVE_REQUESTS_KEY) || "0");
  localStorage.setItem(ACTIVE_REQUESTS_KEY, String(Math.max(0, n - 1)));
}

// === Notifier global (toast/banner) ===
export function notify({ type = "info", message = "", status = null, payload = null } = {}) {
  try {
    window.dispatchEvent(
      new CustomEvent("notify", { detail: { type, message, status, payload, ts: Date.now() } }),
    );
  } catch {}
}

// --- helpers de token ---
function getToken() {
  return (
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") || // extra
    sessionStorage.getItem("access_token") ||
    sessionStorage.getItem("token") // extra
  );
}


// Lee seguro como JSON si corresponde; si no, devuelve texto.
// (No consume el stream original porque usa .clone())
async function safeJson(res) {
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (ct.includes("application/json")) {
    try {
      return await res.clone().json();
    } catch {
      // cae a texto
    }
  }
  try {
    return await res.clone().text();
  } catch {
    return null;
  }
}

function isAbsoluteSessionExpired(body) {
  if (!body) return false;

  if (typeof body === "string") {
    return body.includes("absolute_session_expired");
  }

  return (
    body.error === "absolute_session_expired" ||
    body.message === "absolute_session_expired" ||
    body.detail === "absolute_session_expired" ||
    body.error_description === "absolute_session_expired"
  );
}

function handleUnauthorized(body) {
  if (isAbsoluteSessionExpired(body)) {
    logout();
    return true;
  }

  if (isTokenExpired()) {
    logout();
    return true;
  }

  console.warn("[http] 401 recibido, pero el token actual todavía vive. No se cierra sesión.");
  return false;
}

// --- wrapper base (compatible con fetch) ---
export function apiFetch(url, opts = {}) {
  const isLoginCall = typeof url === "string" && url.includes("/login");

  const headers = {
    Accept: "application/json",
    ...(opts.headers || {}),
  };

  // agrega Authorization salvo para /login
  if (!isLoginCall && !headers.Authorization) {
    const tk = getToken();
    if (tk) headers.Authorization = `Bearer ${tk}`;
  }

  return fetch(url, { ...opts, headers }).then(async (res) => {
    if (res.status === 401) {
      const body = await safeJson(res);

      handleUnauthorized(body);

      const err = new Error("Unauthorized");
      err.status = 401;
      err.body = body;
      throw err;
    }

    // 403: permisos insuficientes → evento, NO redirige
    if (res.status === 403) {
      window.dispatchEvent(new CustomEvent("auth:forbidden"));
      const err = new Error("Forbidden");
      err.status = 403;
      err.body = await safeJson(res);
      throw err;
    }

    return res;
  });
}

// versión que ya devuelve JSON o lanza error con status
// opts.silent === true → NO dispara notify global en error
export async function apiFetchJson(url, opts = {}) {
  const res = await apiFetch(url, opts);
  if (res.status === 204) return null;

  const data = await safeJson(res);

  if (!res.ok) {
    // Si el backend devuelve texto plano, lo usamos “tal cual”.
    // Si devuelve JSON, probamos campos comunes; si no hay, caemos a "HTTP <status>".
    const msg =
      typeof data === "string"
        ? data
        : (data && (data.message || data.error_description || data.detail || data.title)) ||
          //data && (data.message || data.error || data.detail || data.title)) ||
          `HTTP ${res.status}`;

    const err = new Error(msg);
    err.status = res.status;
    err.body = data;
    if (typeof data === "string") err.bodyText = data;

    // 🔔 Notificación global (salvo que el caller pida silencio)
    if (!opts?.silent) {
      notify({ type: "error", message: msg, status: res.status, payload: data });
    }

    throw err;
  }

  // Éxito: si el cuerpo no es JSON, `safeJson` pudo devolver texto.
  // Nuestro contrato de app es JSON en éxito (o 204); lo devolvemos tal cual.
  return data;
}

// === Helpers de descarga con JWT ===
function _getAuthToken() {
  return (
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    null
  );
}

export function extractFilenameFromDisposition(cd, fallbackName = null) {
  if (!cd) return fallbackName;
  const m = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i.exec(cd);
  if (!m) return fallbackName;
  try {
    return decodeURIComponent(m[1] || m[2]);
  } catch {
    return m[1] || m[2] || fallbackName;
  }
}

/** Descarga un recurso binario (Blob) respetando la inyección de JWT. */
export async function appFetchBlob(
  url,
  fetchOpts = {},
  uiOpts = {}
) {
  const {
    showLoading = true,
    showError = true,
    loadingTitle = "Procesando…",
    loadingMessage = "Por favor, espere sin cerrar ni recargar la página.",
  } = uiOpts;

  incActiveRequest();

  if (showLoading && window.overlay?.open) {
    window.overlay.open({
      mode: "loading",
      title: loadingTitle,
      message: loadingMessage,
    });
  }
  
  try {
    const tok = _getAuthToken();

    const {
      method = "GET",
      headers = {},
      body = null,
      accept = "application/octet-stream, application/json",
    } = fetchOpts;

    const h = {
      Accept: accept,
      ...(headers || {}),
    };

    if (tok) {
      h.Authorization = tok.startsWith("Bearer ")
        ? tok
        : `Bearer ${tok}`;
    }

    const res = await fetch(url, { method, headers: h, body });

    // 🔥 MANEJO DE ERROR (igual que appFetchJson)
    if (!res.ok) {
      if (res.status === 401) {
        const body = await safeJson(res);

        handleUnauthorized(body);

        const err = new Error("Unauthorized");
        err.status = 401;
        err.body = body;
        throw err;
      }
      const errorPayload = await safeJson(res);

      const msg =
        typeof errorPayload === "string"
          ? errorPayload
          : (errorPayload &&
              (errorPayload.error_description ||
               errorPayload.message ||
               errorPayload.detail ||
               errorPayload.title)) ||
            `HTTP ${res.status}`;

      if (showError) {
        notify({
          type: "error",
          message: msg,
          status: res.status,
          payload: errorPayload,
        });
      }

      const err = new Error(msg);
      err.status = res.status;
      err.body = errorPayload;
      throw err;
    }

    // ✅ SOLO si OK parsea blob
    const blob = await res.blob();

    const filename = extractFilenameFromDisposition(
      res.headers.get("content-disposition") ||
      res.headers.get("Content-Disposition"),
      null
    );

    return { blob, filename, response: res };

  } finally {
    decActiveRequest();
    if (showLoading && window.overlay?.close) {
      window.overlay.close();
    }
  }
}

// Ejecuta una request con soporte de overlay + notify centralizado.
// uiOpts:
//  - showLoading: boolean (default true)
//  - showSuccess: boolean (default false)
//  - showNotify: boolean (default true)
//  - loadingTitle / loadingMessage
//  - successTitle / successMessage
//  - onSuccessClose(data): callback luego de cerrar el overlay de éxito
//
// Backend (acuerdo de formato):
//  - Éxito: puede incluir un campo "success" con mensaje legible
//    { "success": "Fue agregado el usuario Fulano", ...restoDTO }
//  - Error: siempre JSON con "error_description":
//    { "error": "conflict", "error_description": "detalle legible" }
export async function appFetchJson(
  url,
  fetchOpts = {},
  uiOpts = {}
) {
  const {
    showLoading = true,
    showSuccess = false,
    showError = true,   // ← antes era showNotify

    loadingTitle = "Procesando…",
    loadingMessage = "Por favor, espere sin cerrar ni recargar la página.",

    successTitle = null,
    successMessage = null,

    onSuccessClose = null,
  } = uiOpts;
  incActiveRequest();
  // 1) LOADING
  if (showLoading && window.overlay?.open) {
    window.overlay.open({
      mode: "loading",
      title: loadingTitle,
      message: loadingMessage,
    });
  }

  try {
    // 2) Request
    const res = await apiFetch(url, fetchOpts);

    // 3) Parse
    const data = await safeJson(res);
 //cierra el en proceso...
    if (showLoading && window.overlay?.close) {
      window.overlay.close();
    }
    if (!res.ok) {
      // ----- ERROR -----
      const msg =
        typeof data === "string"
          ? data
          : (data &&
              (data.error_description ||
               data.message ||
               data.detail ||
               data.title)) ||
            `HTTP ${res.status}`;
      // Mostrar overlay ERROR via notify
      if (showError) {
        notify({
          type: "error",
          message: msg,
          status: res.status,
          payload: data,
        });
      }

      const err = new Error(msg);
      err.status = res.status;
      err.body = data;
      if (typeof data === "string") err.bodyText = data;
      throw err;
    }

   
    // Mensaje de éxito:
    // 1) si el backend manda data.success (string), usamos ese
    // 2) si no, usamos successMessage o un default genérico
    const successMsg =
      (data && typeof data === "object" && typeof data.success === "string"
        ? data.success
        : successMessage) ||
      "La operación se realizó correctamente.";

    const successTitleFinal = successTitle || "Operación realizada";

    // ✅ En vez de overlay.open(mode:"success"), usamos notify SUCCESS
    if (showSuccess) {
      notify({
        type: "success",
        title: successTitleFinal,  // opcional, por si tu listener lo usa
        message: successMsg,
        status: res.status,
        payload: data,
      });

      // Y acá seguimos respetando tu callback de cierre del modal
      if (onSuccessClose) {
        try {
          onSuccessClose(data);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error("onSuccessClose error:", e);
        }
      }
    }
    // ❌ OJO: ya NO llames notify success en otro lado, ni overlay.open success.
    // Nada más retorna el data:
    
    return data;
  } catch (e) {
    if (showLoading && window.overlay?.close) {
      window.overlay.close();
    }
    
    throw e;
  } finally {
    decActiveRequest();
  }
}
